<template>
  <div class="tu-scope mc-page" :class="{ 'page-section': !embedded }">
    <div :class="embedded ? '' : containerClass">
      <h2 v-if="!embedded" class="text-center mb-2">Programs</h2>
      <p v-if="!embedded" class="text-center mb-4">Every program you're applying to from the paths you're enrolled in, with its scorecard.</p>

      <div v-if="isLoading" class="mc-grid">
        <div v-for="n in 2" :key="`sk-${n}`" class="mc-card mc-card--skeleton"><b-skeleton width="60%" /><b-skeleton width="40%" /><b-skeleton height="8px" /></div>
      </div>

      <div v-else-if="rows.length" class="mc-grid">
        <article v-for="row in rows" :key="row.key" class="mc-card">
          <div class="mc-card__top">
            <span class="mc-card__logo" aria-hidden="true">
              <img v-if="get(row.uop, 'university.small_logo_url')" :src="row.uop.university.small_logo_url" alt="" />
              <md-icon v-else>school</md-icon>
            </span>
            <div class="mc-card__copy">
              <h3>{{ get(row.uop, 'university.name', 'University') }}</h3>
              <p v-if="uniLocation(row.uop.university)">{{ uniLocation(row.uop.university) }}</p>
            </div>
            <span class="mc-ring" :style="ringStyle(progressFor(row.uop).percent)" :aria-label="`${progressFor(row.uop).completed} of ${progressFor(row.uop).total} steps done`">
              <span class="mc-ring__inner">{{ progressFor(row.uop).completed }}/{{ progressFor(row.uop).total }}</span>
            </span>
          </div>

          <p class="mc-card__program">
            <span class="tu-credential-badge">{{ get(row.uop, 'program.abbr') || get(row.uop, 'program.title') }}</span>
            {{ get(row.uop, 'program.title', '') }}
          </p>

          <router-link v-if="row.pathway" :to="{ name: 'student-pathway-detail', params: { id: row.pathway.id } }" class="mc-card__path">
            <md-icon>directions</md-icon> {{ row.pathway.title }}
          </router-link>

          <div class="mc-card__facts">
            <div>
              <span><small>Steps complete</small><strong>{{ progressFor(row.uop).completed }}/{{ progressFor(row.uop).total }}</strong></span>
            </div>
            <div>
              <span><small>Points earned</small><strong>{{ $n(progressFor(row.uop).points) }}</strong></span>
            </div>
          </div>

          <div class="mc-card__bar" role="progressbar" :aria-valuenow="progressFor(row.uop).percent" aria-valuemin="0" aria-valuemax="100" :aria-label="`${progressFor(row.uop).percent}% of checklist complete`">
            <span :style="{ width: progressFor(row.uop).percent + '%' }"></span>
          </div>
          <p class="mc-card__bar-label" :class="{ 'is-done': isDone(row.uop) }">
            <template v-if="isDone(row.uop)"><md-icon>check_circle</md-icon> Scorecard complete</template>
            <template v-else>{{ progressFor(row.uop).percent }}% complete</template>
          </p>

          <div class="mc-card__actions">
            <router-link :to="checklistRoute(row.uop)" class="tu-btn tu-btn--primary">
              Apply now
            </router-link>
          </div>
        </article>
      </div>

      <div v-else class="tu-empty">
        <md-icon>assignment_turned_in</md-icon>
        <h4 class="tu-empty-title">No programs yet</h4>
        <p class="tu-empty-text">Enroll in a path and each program you pick appears here with its scorecard.</p>
        <router-link :to="{ name: 'student-dashboard' }" class="tu-btn tu-btn--primary">Explore paths</router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { get } from 'lodash';
import { mapActions } from 'vuex';
import Page from '@/components/Page.vue';

/**
 * Checklist: one card per program on every path the student is enrolled in,
 * with live checklist progress. Same card as the path page's Checklist section.
 */
export default {
  name: 'MyChecklists',
  extends: Page,

  // Embedded inside My Info: no page wrapper or heading.
  props: {
    embedded: { type: Boolean, default: false },
  },

  data() {
    return {
      title: 'Programs',
      isLoading: true,
      enrollments: [],
      progressMap: {},
    };
  },

  computed: {
    rows() {
      const seen = new Set();
      const rows = [];
      this.enrollments.forEach(enrollment => {
        (get(enrollment, 'uni_offered_programs', []) || []).forEach(uop => {
          if (!uop || !uop.university || !uop.program) return;
          const key = this.progressKey(get(uop, 'university.id'), get(uop, 'program.id'));
          if (seen.has(key)) return;
          seen.add(key);
          rows.push({ key, uop, pathway: get(enrollment, 'pathway') || null });
        });
      });
      return rows;
    },
  },

  methods: {
    get,
    ...mapActions('pathways', ['getMyPathwayEnrollments']),
    ...mapActions('student', ['getBulkUniversityChecklistScore']),

    uniLocation(university) {
      return [get(university, 'city'), get(university, 'state')].filter(Boolean).join(', ');
    },

    progressKey(universityId, programId) {
      return `${Number(universityId)}-${Number(programId)}`;
    },

    progressFor(uop) {
      const entry = this.progressMap[this.progressKey(get(uop, 'university.id'), get(uop, 'program.id'))] || {};
      const total = Number(entry.total) || 0;
      const completed = Number(entry.completed) || 0;
      let percent = Number(entry.percent);
      if (!Number.isFinite(percent)) percent = total ? (completed / total) * 100 : 0;
      percent = Math.max(0, Math.min(100, Math.round(percent)));
      return { total, completed, percent, points: Number(entry.points) || 0 };
    },

    isDone(uop) {
      const pr = this.progressFor(uop);
      return pr.total > 0 && pr.completed >= pr.total;
    },

    ringStyle(percent) {
      return { background: `conic-gradient(var(--tu-blue) ${percent}%, var(--tu-surface-2) 0)` };
    },

    checklistRoute(uop) {
      return {
        name: 'std-cc-uni-checklist-detail',
        params: { slug: get(uop, 'university.slug') },
        query: { scope: 'program', program_id: get(uop, 'program.id') },
      };
    },

    async fetchProgress() {
      const payload = this.rows
        .map(r => ({ university_id: Number(get(r.uop, 'university.id')), program_id: Number(get(r.uop, 'program.id')) }))
        .filter(p => p.university_id && p.program_id);
      if (!payload.length) {
        this.progressMap = {};
        return;
      }
      try {
        const response = await this.getBulkUniversityChecklistScore(payload);
        const results = get(response, 'data.data.results') || get(response, 'data.results') || [];
        const map = {};
        results.forEach(item => {
          if (!item) return;
          const progress = item.checklist_progress || {};
          map[this.progressKey(item.university_id, item.program_id)] = {
            points: item.earned_points_sum,
            completed: progress.completed_checks,
            total: progress.total_enabled_checks,
            percent: progress.completion_percent,
          };
        });
        this.progressMap = map;
      } catch (_err) {
        this.progressMap = {};
      }
    },

    async fetchAll() {
      this.isLoading = true;
      try {
        const response = await this.getMyPathwayEnrollments({ limit: 200 });
        this.enrollments = get(response, 'data.results', []) || [];
        await this.fetchProgress();
      } catch (_err) {
        this.enrollments = [];
      }
      this.isLoading = false;
    },
  },

  mounted() {
    this.fetchAll();
  },
};
</script>

<style lang="scss" scoped>
.mc-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
}
.mc-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 18px;
  background: #fff;
  border: 1px solid var(--tu-card-border);
  border-radius: 14px;
}
.mc-card--skeleton { gap: 14px; min-height: 180px; }
.mc-card__top { display: flex; align-items: center; gap: 12px; }
.mc-card__logo {
  display: grid;
  place-items: center;
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: var(--tu-surface-2);
  overflow: hidden;
  img { width: 100%; height: 100%; object-fit: contain; }
  .md-icon { color: var(--tu-blue); }
}
.mc-card__copy {
  min-width: 0;
  flex: 1;
  h3 { margin: 0; font-size: 16px; font-weight: 700; letter-spacing: -0.01em; line-height: 1.25; color: var(--tu-ink); }
  p { margin: 2px 0 0; font-size: 13px; color: var(--tu-ink-mute); }
}
.mc-card__program { display: flex; align-items: center; gap: 8px; margin: 0; font-size: 14px; font-weight: 500; color: var(--tu-ink); }
.mc-card__path {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  font-size: 13px;
  font-weight: 600;
  color: var(--tu-blue) !important;
  text-decoration: none !important;
  .md-icon { font-size: 16px; color: inherit; }
  &:hover { text-decoration: underline !important; }
}
.mc-ring { flex: none; width: 48px; height: 48px; border-radius: 50%; display: grid; place-items: center; }
.mc-ring__inner {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #fff;
  display: grid;
  place-items: center;
  font-size: 11px;
  font-weight: 700;
  color: var(--tu-ink);
}
.mc-card__facts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  > div { display: flex; align-items: center; gap: 10px; min-width: 0; }
  > div > .md-icon {
    flex: none;
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    margin: 0;
    border-radius: 50%;
    background: var(--tu-blue-tint);
    font-size: 20px;
    color: var(--tu-blue-deep) !important;
  }
  small { display: block; font-size: 12px; color: var(--tu-ink-soft); }
  strong { display: block; margin-top: 2px; font-size: 15px; font-weight: 700; color: var(--tu-ink); }
}
.mc-card__bar { position: relative; height: 8px; border-radius: 999px; background: var(--tu-surface-2); overflow: hidden; }
.mc-card__bar span { display: block; height: 100%; border-radius: 999px; background: var(--tu-blue); transition: width 0.4s ease; }
.mc-card__bar-label { margin: -4px 0 0; font-size: 12px; font-weight: 600; color: var(--tu-ink-soft); display: flex; align-items: center; gap: 4px; }
.mc-card__bar-label.is-done { color: var(--tu-green); .md-icon { font-size: 16px; color: var(--tu-green); } }
.mc-card__actions {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 2px;
  .tu-btn { flex: 1; min-height: 40px; font-size: 14px; padding: 7px 14px; white-space: nowrap; }
}
.tu-empty .tu-btn { margin-top: 14px; }
</style>
